import React from 'react';
import ReactDOM from 'react-dom/client';
import { GlobalErrorBoundary, installChunkReloadHandler } from '@pc/ui';
import { initI18n } from '@pc/i18n';
import { initAnalytics, installRouteTrackerVanilla } from '@pc/analytics-client';
import { getSession } from '@pc/db';
import { App } from './App';
import { env } from './env';
import './index.css';

// Backstop for the stale-chunk failure mode: any `import('./foo')`
// that 404s because the user is on the old shell after a deploy will
// trigger a debounced hard reload instead of bubbling an "Unexpected
// Application Error!" stack trace to the user. Code-split routes
// already use `lazyWithReload` (see App.tsx); this listener catches
// every OTHER dynamic import in the bundle (i18n, third-party libs,
// the public-storefront pre-route below). MUST run before any React
// mount so the listener is attached when the first chunk lands.
installChunkReloadHandler();

// react-i18next bootstrap. The buyer settings page persists their choice
// to core.users.preferred_locale; unauthenticated visitors fall back to
// browser Accept-Language.
initI18n({ app: 'shop' });

// Self-hosted pageview analytics. Anonymous-friendly: getSession returns
// null when there's no logged-in operator (the public-storefront branch)
// and the ingest function degrades to anonymous. Required prerequisite
// for credible "ad shown to N visitors" denominators.
const analyticsClient = initAnalytics({
  app: 'shop',
  supabaseUrl: env.supabaseUrl,
  anonKey: env.supabaseAnonKey,
  getSession: async () => {
    try {
      const s = await getSession();
      return s ? { access_token: s.access_token } : null;
    } catch {
      return null;
    }
  },
});

const rootEl = document.getElementById('root');
if (!rootEl) throw new Error('Root element #root not found.');

// -------------------------------------------------------------------------
// MARKETPLACE LOCKDOWN (2026-06-08, "coming soon").
//
// The shop is intentionally shut down for now. We mount a single blocking
// "coming soon" popup ABOVE every other entry branch (the operator router,
// the public-storefront pre-route, the signed-in redirect) so nobody can
// browse, check out, or load a public storefront while we finish it.
//
// To re-open: flip `SHOP_COMING_SOON` to false (and re-enable `shopModule`
// in apps/web/src/shell/moduleRegistry.ts). While it is `true`, the
// lockdown is the first branch of the dispatch chain below, so none of the
// real app branches (public storefront, signed-in redirect, operator
// router) ever mount.
// -------------------------------------------------------------------------
const SHOP_COMING_SOON = true;

// -------------------------------------------------------------------------
// Public-storefront pre-route (Bug 1 fix, 2026-05-19).
//
// React Router v6's `compilePath` only treats `:name` as a dynamic
// segment when it directly follows a `/` (regex `\/:([\w-]+)` --
// see `@remix-run/router` dist/router.js). The literal `@` between
// `/` and `:` in the pattern `/@:slug` breaks that match, so the
// router fell through to the `*` catch-all and ultimately rendered
// `RequireAuth`. The previous worker's claim that the route was
// "registered ahead of RequireAuth" was correct in source order, but
// the path PATTERN itself never matched any real URL.
//
// Rather than try to coerce React Router into matching `/@:slug`
// (which would require either a splat at root or restructuring the
// nested `:slug` route under the auth shell), we detect the URL
// here, before ANY auth / Supabase client is initialized, and mount
// a minimal app that contains only the public storefront. This:
//
//   * Removes the auth path entirely for off-platform visitors --
//     they never load `initSupabase`, `AuthProvider`, or anything
//     that could redirect them.
//   * Keeps the shop SPA bundle the same for the rest of the app.
//   * Works for logged-in operators too: they hit `/@<slug>`, this
//     branch fires, and they see the public storefront (intentional
//     per the feature spec so vendors can preview without signing
//     out).
//
// Slug grammar mirrors `SLUG_RE` in `supabase/functions/public-
// storefront/index.ts`: lowercase alphanumeric + dash / underscore,
// 1-63 chars, must start with [a-z0-9].
//
// Two URL shapes are public-storefront territory:
//   /@<vendor>            -> PublicStorefrontPage (catalog)
//   /@<vendor>/<product>  -> PublicProductDetailPage (PDP)
//
// Both ride the same auth-free PublicStorefrontApp shell. The PDF
// price-list links generated by `vendor-price-list-pdf/index.ts`
// land on the second pattern, so off-platform PDF readers tapping a
// "View" button never bounce into the auth-gated app shell.
// -------------------------------------------------------------------------
const PUBLIC_STOREFRONT_PATTERN =
  /^\/@([a-z0-9][a-z0-9_-]{0,62})(?:\/([a-z0-9][a-z0-9_-]{0,62}))?\/?$/i;
const publicMatch = PUBLIC_STOREFRONT_PATTERN.exec(window.location.pathname);

// -------------------------------------------------------------------------
// Signed-in operators viewing a public-share URL (BUG fix, 2026-05-20).
//
// `/@<slug>` is the public, off-platform shareable storefront. The pre-
// route above mounts PublicStorefrontApp without AuthProvider for it,
// which was intentional for anonymous visitors and originally also for
// authenticated operators "previewing what their clients will see". In
// practice operators perceived that branch as a silent logout: their
// auth nav disappeared on hard refresh / pasted-link arrivals, even
// though `localStorage['pc-shop.auth']` was still populated and they
// were one click away from their inbox.
//
// Resolution: when a non-expired session blob is sitting in
// `localStorage['pc-shop.auth']`, treat `/@<slug>` as an alias for the
// in-app authenticated vendor profile at `/<slug>` and redirect there
// BEFORE either app shell mounts. That keeps the public storefront as
// the truly-anonymous experience (off-platform visitors, search bots,
// shared-link previews) while signed-in operators land on the
// authenticated VendorProfilePage inside ShopShell. The redirect
// preserves any `?preview=...` query string for future explicit-preview
// support; an operator who actually wants to QA the public view can
// open the link in an incognito tab.
//
// Storage key MUST stay in sync with `initSupabase({ storageKey })` in
// apps/shop/src/App.tsx and `SHOP_AUTH_STORAGE_KEY` in
// apps/shop/src/auth/AuthProvider.tsx.
// -------------------------------------------------------------------------
const SHOP_AUTH_STORAGE_KEY = 'pc-shop.auth';

function hasPersistedSession(): boolean {
  try {
    const raw = window.localStorage.getItem(SHOP_AUTH_STORAGE_KEY);
    if (!raw) return false;
    const parsed = JSON.parse(raw) as {
      access_token?: unknown;
      refresh_token?: unknown;
      expires_at?: unknown;
    } | null;
    if (
      !parsed ||
      typeof parsed.access_token !== 'string' ||
      typeof parsed.refresh_token !== 'string'
    ) {
      return false;
    }
    if (
      typeof parsed.expires_at === 'number' &&
      parsed.expires_at * 1000 <= Date.now()
    ) {
      return false;
    }
    return true;
  } catch {
    return false;
  }
}

if (SHOP_COMING_SOON) {
  // Marketplace lockdown: mount the blocking "coming soon" popup and stop.
  // This wins over every other branch so no browse / checkout / storefront
  // surface loads. See the SHOP_COMING_SOON note above.
  void (async () => {
    const { ComingSoonGate } = await import('./ComingSoonGate');
    ReactDOM.createRoot(rootEl).render(
      <React.StrictMode>
        <GlobalErrorBoundary>
          <ComingSoonGate />
        </GlobalErrorBoundary>
      </React.StrictMode>,
    );
  })();
} else if (publicMatch && hasPersistedSession()) {
  // Redirect signed-in operators from the public-share URL to the
  // in-app authenticated vendor profile or product detail. The auth
  // shell already routes `:slug` -> VendorProfilePage and
  // `:slug/:productSlug` -> ProductDetailPage, so we just strip the
  // leading `@`. `location.replace` so the public URL doesn't pile up
  // in the back-button history.
  const slug = publicMatch[1]!.toLowerCase();
  const productSlug = publicMatch[2]?.toLowerCase() ?? null;
  const search = window.location.search;
  const hash = window.location.hash;
  const target = productSlug ? `/${slug}/${productSlug}` : `/${slug}`;
  window.location.replace(`${target}${search}${hash}`);
} else if (publicMatch) {
  // Truly-anonymous visitor (or expired session): render the public
  // storefront without paying for the (broken-for-`/@:slug`) auth-aware
  // router. Dynamic import so the public storefront chunk stays separate
  // from the operator-app entry chunk in the same way the lazy() router
  // route did.
  void (async () => {
    const [{ PublicStorefrontApp }] = await Promise.all([
      import('./PublicStorefrontApp'),
    ]);
    const slug = publicMatch[1]!.toLowerCase();
    const productSlug = publicMatch[2]?.toLowerCase() ?? null;
    // Public storefront has no router — fire a single pageview now and
    // tag it with the vendor_slug so analytics rolls up cleanly.
    analyticsClient.trackPageview({
      vendor_slug: slug,
      path_template: productSlug ? '/@:slug/:productSlug' : '/@:slug',
    });
    installRouteTrackerVanilla(analyticsClient, { fireImmediately: false, mode: 'static' });
    ReactDOM.createRoot(rootEl).render(
      <React.StrictMode>
        <GlobalErrorBoundary>
          <PublicStorefrontApp slug={slug} productSlug={productSlug} />
        </GlobalErrorBoundary>
      </React.StrictMode>,
    );
  })();
} else {
  ReactDOM.createRoot(rootEl).render(
    <React.StrictMode>
      <GlobalErrorBoundary>
        <App />
      </GlobalErrorBoundary>
    </React.StrictMode>,
  );
}
